/**
 * Editar o mapa: as mudanças, sem React, sobre a configuração da automação.
 *
 * A tela guarda uma cópia da configuração (gatilho, condição e grafo) e cada
 * gesto vira uma função daqui: pôr um bloco, ligar uma saída, mover, tirar.
 * Salvar manda a cópia inteira ao serviço (`PATCH /v1/automacoes/{id}`), que
 * cria a versão nova; nada muda no serviço antes disso.
 *
 * ── O que se edita ────────────────────────────────────────────────────────
 *
 * A tela edita o que mostra: numa automação no ar que já foi editada, a
 * alteração pendente; senão, a versão de agora (a mesma régua do backend em
 * `editarAutomacao`).
 */

import type { AutomacaoDaApi, BlocosDoEditor } from '@cliqcrm/contracts';
import { ID_DO_COMECO, rotuloDaSaida, type LigacaoDoGrafo, type NoDoGrafo } from './mapa';
import {
  proximoResultadoLivre,
  resultadosUsados,
  rotuloDoResultado,
} from './resultado-da-consulta';
import {
  comporMenus,
  conversaDoMenuValida,
  ehMenu,
  idNaTela,
  rotuloDaSaidaDoMenu,
  saidasDoMenu,
  temRamos,
} from './menu';

type Grafo = NonNullable<AutomacaoDaApi['grafo']>;

export type Configuracao = {
  gatilho: AutomacaoDaApi['gatilho'];
  condicao: AutomacaoDaApi['condicao'];
  grafo: Grafo;
};

/** A configuração que a tela edita, ou `null` quando a automação não tem mapa. */
export function configuracaoDe(a: AutomacaoDaApi): Configuracao | null {
  const fonte = a.pendente ?? a;
  // O menu com ramos é um bloco só na tela: a espera e a escolha dele se juntam a ele.
  return fonte.grafo
    ? comporMenus({ gatilho: fonte.gatilho, condicao: fonte.condicao, grafo: fonte.grafo })
    : null;
}

/** A versão que a tela leu: a da pendente, quando há. */
export function versaoEditada(a: AutomacaoDaApi): number {
  return a.pendente?.versao ?? a.versao;
}

// ── As saídas ────────────────────────────────────────────────────────────

/**
 * A continuação: a saída sem rótulo. O serviço lê a ligação sem `quando` como
 * `padrao`, e é a mesma saída; ela vai sem `quando`. Na escolha, ela é o
 * "Outro caso"; nos outros blocos, o "Próximo".
 */
export const PROXIMO = 'padrao';

/** A saída de uma ligação, como o serviço a lê. */
export function saidaDaLigacao(l: LigacaoDoGrafo): string {
  return l.quando ?? PROXIMO;
}

/** O nome de uma saída na tela, pelo tipo do bloco. */
export function rotuloDaSaidaDoBloco(saida: string, tipo: string): string {
  if (saida === PROXIMO) return tipo === 'escolha' ? 'Outro caso' : 'Próximo';
  return rotuloDaSaida(saida) ?? saida;
}

/** O nome de uma saída deste bloco: no menu, o rótulo da opção. */
export function rotuloDaSaidaDoNo(no: NoDoGrafo | undefined, saida: string): string {
  if (ehMenu(no)) {
    const doMenu = rotuloDaSaidaDoMenu(no, saida);
    if (doMenu) return doMenu;
  }
  return rotuloDaSaidaDoBloco(saida, no?.tipo ?? '');
}

/**
 * As saídas de um bloco: as do catálogo (sem o modelo `caso:<valor>`) e, na
 * escolha, os casos que o mapa já usa e os novos que a pessoa acrescentou.
 */
export function saidasDoNo(
  no: NoDoGrafo,
  ligacoes: readonly LigacaoDoGrafo[],
  blocos: BlocosDoEditor | undefined,
  casosNovos: readonly string[] = [],
): string[] {
  if (ehMenu(no)) {
    // O menu sai por opção; um "Próximo" já ligado (menu de antes dos ramos) continua.
    const usadas = ligacoes.filter((l) => l.de === no.id).map(saidaDaLigacao);
    const todas = [...new Set([...saidasDoMenu(no), ...usadas])];
    return todas.length > 0 ? todas : [PROXIMO];
  }
  const doCatalogo = (blocos?.nos.find((n) => n.tipo === no.tipo)?.saidas ?? []).filter(
    (s) => !s.includes('<'),
  );
  const casos =
    no.tipo === 'escolha'
      ? [
          ...casosNovos,
          ...ligacoes
            .filter((l) => l.de === no.id && l.quando?.startsWith('caso:'))
            .map((l) => l.quando!),
        ]
      : [];
  const todas = [...new Set([...casos, ...doCatalogo])];
  return todas.length > 0 ? todas : [PROXIMO];
}

// ── Os blocos ────────────────────────────────────────────────────────────

/** Um bloco da paleta: um tipo do catálogo, ou uma ação (o bloco `acao` dela). */
export type ItemDaPaleta = {
  chave: string;
  titulo: string;
  descricao: string;
  grupo: 'acao' | 'logica' | 'espera' | 'dados';
  tipo: NoDoGrafo['tipo'];
  acao?: string;
};

/**
 * O que a paleta oferece: as ações do catálogo e os blocos de lógica, espera e
 * dados. O catálogo já vem sem os de SQL para quem não os monta. O "para cada"
 * ainda não entra: ele carrega um mapa dentro de si, que o editor não desenha.
 */
export function itensDaPaleta(blocos: BlocosDoEditor | undefined): ItemDaPaleta[] {
  if (!blocos) return [];
  const GRUPO: Record<string, ItemDaPaleta['grupo']> = {
    condicao: 'logica',
    escolha: 'logica',
    esperar_tempo: 'espera',
    esperar_evento: 'espera',
    consulta_sql: 'dados',
  };
  const acoes: ItemDaPaleta[] = blocos.acoes.map((a) => ({
    chave: `acao:${a.acao}`,
    titulo: a.titulo,
    descricao: '',
    grupo: 'acao',
    tipo: 'acao',
    acao: a.acao,
  }));
  const outros: ItemDaPaleta[] = blocos.nos
    .filter((n) => n.tipo in GRUPO)
    .map((n) => ({
      chave: n.tipo,
      titulo: n.titulo,
      descricao: n.descricao,
      grupo: GRUPO[n.tipo]!,
      tipo: n.tipo as NoDoGrafo['tipo'],
    }));
  return [...acoes, ...outros];
}

/** A paleta filtrada pela busca, sem acento: "condicao" acha "Condição". */
export function buscarNaPaleta(itens: readonly ItemDaPaleta[], busca: string): ItemDaPaleta[] {
  const termo = semAcento(busca);
  return itens.filter((i) => termo === '' || semAcento(i.titulo).includes(termo));
}

/** O texto sem acento e sem caixa, para a busca da paleta. */
export function semAcento(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
}

/** Um id que o grafo ainda não tem: `acao_1`, `condicao_2`… */
export function novoId(grafo: Grafo, base: string): string {
  const usados = new Set(grafo.nos.map((n) => n.id));
  let i = 1;
  while (usados.has(`${base}_${i}`)) i += 1;
  return `${base}_${i}`;
}

function blocoNovo(
  item: ItemDaPaleta,
  id: string,
  posicao: { x: number; y: number },
  blocos: BlocosDoEditor | undefined,
): NoDoGrafo {
  switch (item.tipo) {
    case 'acao':
      return { id, tipo: 'acao', acao: item.acao!, parametros: {}, posicao };
    case 'condicao':
      return { id, tipo: 'condicao', expressao: '', posicao };
    case 'escolha':
      return { id, tipo: 'escolha', expressao: '', posicao };
    case 'esperar_tempo':
      return { id, tipo: 'esperar_tempo', milissegundos: 3_600_000, posicao };
    case 'esperar_evento':
      return {
        id,
        tipo: 'esperar_evento',
        evento: blocos?.eventos.find((e) => !e.em_breve)?.evento ?? '',
        correlacao: '',
        posicao,
      };
    case 'consulta_sql':
      return {
        id,
        tipo: 'consulta_sql',
        operacao_id: '',
        parametros: [],
        resultado_em: 'linhas',
        posicao,
      };
    case 'para_cada':
      throw new Error('o "para cada" não entra pela paleta');
  }
}

export function adicionarBloco(
  c: Configuracao,
  item: ItemDaPaleta,
  posicao: { x: number; y: number },
  blocos: BlocosDoEditor | undefined,
): { configuracao: Configuracao; id: string } {
  const id = novoId(c.grafo, item.tipo);
  const criado = blocoNovo(item, id, posicao, blocos);
  // A consulta nova guarda as linhas num resultado que nenhuma outra usa.
  const no =
    criado.tipo === 'consulta_sql'
      ? { ...criado, resultado_em: proximoResultadoLivre(c.grafo) }
      : criado;
  return { configuracao: { ...c, grafo: { ...c.grafo, nos: [...c.grafo.nos, no] } }, id };
}

/**
 * Duplicar: uma cópia do bloco, com id novo, um pouco abaixo e à direita, e
 * sem ligações (a cópia é o ponto de partida de outro caminho). O "para cada"
 * não se duplica: os blocos de dentro dele teriam o mesmo id da original.
 */
export function duplicarBloco(
  c: Configuracao,
  id: string,
): { configuracao: Configuracao; id: string } | null {
  const no = c.grafo.nos.find((n) => n.id === id);
  if (!no || no.tipo === 'para_cada') return null;
  const novo = novoId(c.grafo, no.tipo);
  const onde = no.posicao ?? { x: 0, y: 0 };
  const copia = {
    ...structuredClone(no),
    id: novo,
    posicao: { x: onde.x + 32, y: onde.y + 96 },
    // A cópia de uma consulta não pode guardar no mesmo resultado da original.
    ...(no.tipo === 'consulta_sql' ? { resultado_em: proximoResultadoLivre(c.grafo) } : {}),
  };
  return {
    configuracao: { ...c, grafo: { ...c.grafo, nos: [...c.grafo.nos, copia as NoDoGrafo] } },
    id: novo,
  };
}

/** O próximo passo de uma saída: o bloco novo entra à direita, já ligado a ela. */
export function adicionarDepois(
  c: Configuracao,
  de: { id: string; posicao: { x: number; y: number } },
  saida: string,
  ordem: number,
  item: ItemDaPaleta,
  blocos: BlocosDoEditor | undefined,
): { configuracao: Configuracao; id: string } {
  const posicao = { x: de.posicao.x + 300, y: de.posicao.y + ordem * 120 };
  const { configuracao, id } = adicionarBloco(c, item, posicao, blocos);
  return { configuracao: ligar(configuracao, de.id, saida, id), id };
}

/**
 * Tirar blocos leva as ligações deles junto. Tirar o primeiro deixa o começo
 * sem destino: vira pendência, até a pessoa ligar o começo a outro bloco.
 */
export function removerBlocos(c: Configuracao, ids: readonly string[]): Configuracao {
  const fora = new Set(ids);
  fora.delete(ID_DO_COMECO);
  return {
    ...c,
    grafo: {
      inicio: fora.has(c.grafo.inicio) ? '' : c.grafo.inicio,
      nos: c.grafo.nos.filter((n) => !fora.has(n.id)),
      ligacoes: c.grafo.ligacoes.filter((l) => !fora.has(l.de) && !fora.has(l.para)),
    },
  };
}

/**
 * Ligar uma saída a um bloco. Uma saída leva a um bloco só: ligar de novo
 * troca o destino. Do começo, a ligação é o início do grafo.
 */
export function ligar(c: Configuracao, de: string, saida: string, para: string): Configuracao {
  if (de === para) return c;
  if (de === ID_DO_COMECO) return { ...c, grafo: { ...c.grafo, inicio: para } };
  const quando = saida === PROXIMO ? undefined : saida;
  const outras = c.grafo.ligacoes.filter((l) => !(l.de === de && saidaDaLigacao(l) === saida));
  const nova: LigacaoDoGrafo = quando ? { de, para, quando } : { de, para };
  return { ...c, grafo: { ...c.grafo, ligacoes: [...outras, nova] } };
}

/** Desligar: a saída fica sem destino (o caminho termina ali). */
export function desligar(c: Configuracao, de: string, saida: string): Configuracao {
  if (de === ID_DO_COMECO) return { ...c, grafo: { ...c.grafo, inicio: '' } };
  return {
    ...c,
    grafo: {
      ...c.grafo,
      ligacoes: c.grafo.ligacoes.filter((l) => !(l.de === de && saidaDaLigacao(l) === saida)),
    },
  };
}

export function mover(c: Configuracao, id: string, x: number, y: number): Configuracao {
  return {
    ...c,
    grafo: {
      ...c.grafo,
      nos: c.grafo.nos.map((n) => (n.id === id ? { ...n, posicao: { x, y } } : n)),
    },
  };
}

/** Muda os campos de um bloco; o tipo e o id não mudam. */
export function alterarBloco(
  c: Configuracao,
  id: string,
  mudanca: Partial<Record<string, unknown>>,
): Configuracao {
  return {
    ...c,
    grafo: {
      ...c.grafo,
      nos: c.grafo.nos.map((n) =>
        n.id === id ? ({ ...n, ...mudanca, id: n.id, tipo: n.tipo } as NoDoGrafo) : n,
      ),
    },
  };
}

/** O máximo de uma espera, como no contrato: um ano bissexto. */
export const ESPERA_MAXIMA_MS = 366 * 24 * 3_600_000;

// ── As pendências ────────────────────────────────────────────────────────

/**
 * O que impede salvar, por bloco (`comeco` para o começo). O serviço confere o
 * resto (as expressões, os ids) e devolve o erro por bloco.
 */
export function pendencias(
  c: Configuracao,
  blocos: BlocosDoEditor | undefined,
  /** Quantos valores cada consulta pede, pelo id: o bloco precisa de todos. */
  valoresDasConsultas: Readonly<Record<string, number>> = {},
): Record<string, string[]> {
  const p: Record<string, string[]> = {};
  const add = (id: string, texto: string): void => {
    p[id] = [...(p[id] ?? []), texto];
  };
  // A rotina sem horário não tem quando rodar (o serviço recusaria ao salvar).
  if (c.gatilho.tipo === 'agenda' && c.gatilho.horarios.length === 0) {
    add(ID_DO_COMECO, 'Diga pelo menos um horário em que a rotina roda.');
  }
  const saidasUsadas = (id: string): string[] =>
    c.grafo.ligacoes.filter((l) => l.de === id).map(saidaDaLigacao);
  if (!c.grafo.inicio || !c.grafo.nos.some((n) => n.id === c.grafo.inicio)) {
    add(ID_DO_COMECO, 'Ligue o começo ao primeiro bloco.');
  }
  if (c.grafo.nos.length === 0) add(ID_DO_COMECO, 'Ponha pelo menos um bloco no mapa.');
  for (const no of c.grafo.nos) {
    switch (no.tipo) {
      case 'acao': {
        const acao = blocos?.acoes.find((a) => a.acao === no.acao);
        for (const parametro of acao?.parametros ?? []) {
          if (parametro.obrigatorio && !(no.parametros[parametro.nome] ?? '').trim()) {
            add(no.id, `Preencha “${parametro.titulo}”.`);
          }
        }
        if (ehMenu(no) && temRamos(no, c.grafo.ligacoes) && !conversaDoMenuValida(no)) {
          add(
            no.id,
            'O menu com opções ligadas espera a resposta na conversa do evento: em “Conversa”, escolha “A conversa do evento”.',
          );
        }
        break;
      }
      case 'condicao': {
        if (!no.expressao.trim()) add(no.id, 'Escreva a regra.');
        const saidas = saidasUsadas(no.id);
        if (!saidas.includes('sim') || !saidas.includes('nao')) {
          add(no.id, 'Ligue as duas saídas, Sim e Não.');
        }
        break;
      }
      case 'escolha':
        if (!no.expressao.trim()) add(no.id, 'Escreva a regra.');
        if (saidasUsadas(no.id).length === 0) add(no.id, 'Ligue pelo menos uma saída.');
        break;
      case 'esperar_evento':
        if (!no.evento) add(no.id, 'Escolha o que esperar.');
        if (!no.correlacao.trim()) add(no.id, 'Diga como reconhecer o evento desta automação.');
        if (saidasUsadas(no.id).includes('tempo_esgotado') && !no.limite_ms) {
          add(no.id, 'A saída "Tempo esgotado" pede um limite de espera.');
        }
        break;
      case 'consulta_sql':
        if (!no.operacao_id) add(no.id, 'Escolha a consulta.');
        if (
          no.parametros.some((v) => !v.trim()) ||
          (valoresDasConsultas[no.operacao_id] ?? no.parametros.length) > no.parametros.length
        ) {
          add(no.id, 'Preencha o que a consulta recebe.');
        }
        if (!/^[a-z_][a-z0-9_]{0,63}$/.test(no.resultado_em)) {
          add(
            no.id,
            'O nome do resultado usa letras minúsculas, números e _, começando por letra.',
          );
        } else if (resultadosUsados(c.grafo, no.id).has(no.resultado_em)) {
          add(
            no.id,
            `Outra consulta já guarda em “${rotuloDoResultado(no.resultado_em)}”: escolha outro, senão uma apaga a outra.`,
          );
        }
        break;
      case 'para_cada':
        if (!no.operacao_id) add(no.id, 'Escolha a consulta.');
        break;
      case 'esperar_tempo':
        if (no.milissegundos > ESPERA_MAXIMA_MS) add(no.id, 'A espera vai até um ano.');
        break;
    }
  }
  return p;
}

/**
 * Os erros do serviço por bloco: `grafo.nos.<id>.<campo>` vira o bloco `id`;
 * o resto (gatilho, condição) fica no começo.
 */
export function errosPorBloco(
  errors: readonly { campo: string; problema: string }[],
  c: Configuracao,
): Record<string, string[]> {
  const p: Record<string, string[]> = {};
  for (const e of errors) {
    const m = /^grafo\.nos\.([A-Za-z0-9_-]+)/.exec(e.campo);
    // O serviço diz o bloco pelo id; a conferência do formato, pela posição na lista.
    const pelaPosicao = m && /^\d+$/.test(m[1]!) ? c.grafo.nos[Number(m[1])]?.id : undefined;
    const pelaId =
      m && c.grafo.nos.some((n) => n.id === m[1] || n.id === idNaTela(m[1]!)) ? m[1] : undefined;
    // A espera e a escolha do menu com ramos são o menu, na tela.
    const id = idNaTela(pelaId ?? pelaPosicao ?? ID_DO_COMECO);
    p[id] = [...(p[id] ?? []), e.problema];
  }
  return p;
}
